// Merge duplicate customers (Pat, Oct 6 2026: "merge the duplicate customers too"). Pick the two,
// choose which one to keep, see what moves, then merge — everything on the duplicate (contacts,
// bids, bid recipients, jobs, POs) moves to the keeper and the duplicate is removed. The server
// side is POST /clients/:keepId/merge {from_id}; GET …?from= previews the counts.
import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../../services/apiClient';

// ── Possible duplicates ──
// Same name once punctuation and company words are dropped; one name the other's initials
// ("NDOT" ↔ "Nevada Department of Transportation"); or mostly the same words ("Reno Tahoe
// International Airport" ↔ "Reno-Tahoe Airport Authority").
const COMPANY_WORDS = /\b(inc|llc|co|corp|corporation|company|the|of|and|ltd|lp|incorporated|construction|contractors?)\b/g;
const words = n => String(n || '').toLowerCase().replace(/&/g, ' and ').replace(/[^a-z0-9 ]+/g, ' ').replace(COMPANY_WORDS, ' ').split(/\s+/).filter(Boolean);
// Both ways: "NDOT" keeps the O of "of"; others drop the small words.
const initials = n => { const w = String(n || '').toLowerCase().replace(/[^a-z ]+/g, ' ').split(/\s+/).filter(Boolean); return [w.map(x => x[0]).join(''), w.filter(x => !/^(of|and|the)$/.test(x)).map(x => x[0]).join('')]; };
export function findDuplicates(clients) {
  const list = (clients || []).filter(c => c && c.name);
  const pairs = [];
  for (let i = 0; i < list.length; i++) for (let j = i + 1; j < list.length; j++) {
    const a = list[i], b = list[j];
    const wa = words(a.name), wb = words(b.name);
    const ka = wa.join(' '), kb = wb.join(' ');
    let why = null;
    if (ka && ka === kb) why = 'same name';
    else if (wa.length === 1 && wa[0].length >= 3 && initials(b.name).includes(wa[0])) why = `${a.name} = ${b.name}'s initials`;
    else if (wb.length === 1 && wb[0].length >= 3 && initials(a.name).includes(wb[0])) why = `${b.name} = ${a.name}'s initials`;
    else {
      const sa = new Set(wa), sb = new Set(wb);
      const shared = [...sa].filter(w => sb.has(w)).length, union = new Set([...sa, ...sb]).size;
      if (shared >= 2 && union && shared / union >= 0.6) why = 'mostly the same words';
    }
    if (why) pairs.push({ a, b, why });
  }
  return pairs;
}

export default function CustomerMerge({ clients, start = null, startOther = null, onMerged, onClose }) {
  const [one, setOne] = useState(start ? start.id : '');
  const [two, setTwo] = useState(startOther ? startOther.id : '');
  const [keep, setKeep] = useState(start ? start.id : '');
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const byId = useMemo(() => Object.fromEntries((clients || []).map(c => [c.id, c])), [clients]);
  const sorted = useMemo(() => [...(clients || [])].sort((x, y) => String(x.name).localeCompare(String(y.name))), [clients]);
  const keepId = keep && (keep === one || keep === two) ? keep : one;
  const fromId = keepId === one ? two : one;
  useEffect(() => {
    setPlan(null); setErr('');
    if (!one || !two || one === two) return;
    apiFetch(`/clients/${keepId}/merge?from=${fromId}`).then(setPlan).catch(e => setErr(e?.error || 'Could not read what would move.'));
  }, [one, two, keepId, fromId]);

  async function merge() {
    const k = byId[keepId], f = byId[fromId];
    if (!window.confirm(`Merge "${f.name}" into "${k.name}"?\n\nEverything on "${f.name}" moves to "${k.name}", and "${f.name}" is removed. This can't be undone.`)) return;
    setBusy(true); setErr('');
    try { const r = await apiFetch(`/clients/${keepId}/merge`, { method: 'POST', body: JSON.stringify({ from_id: fromId }) }); onMerged && onMerged(r.client, fromId); }
    catch (e) { setErr(e?.error || e?.message || 'The merge did not go through.'); }
    setBusy(false);
  }
  const pickStyle = { width: '100%', border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 10px', fontSize: 13.5, background: '#fff' };
  const side = (id, setId) => {
    const c = byId[id];
    return (
      <div style={{ flex: '1 1 260px', border: `2px solid ${id && keepId === id ? '#16a34a' : '#e5e7eb'}`, borderRadius: 12, padding: 12, background: id && keepId === id ? '#f0fdf4' : '#fff' }}>
        <select value={id} onChange={e => setId(e.target.value)} style={pickStyle}>
          <option value="">— pick a customer —</option>
          {sorted.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
        </select>
        {c && (
          <div style={{ fontSize: 12.5, color: '#4b5563', marginTop: 8, lineHeight: 1.5 }}>
            {[c.address1, c.address2].filter(Boolean).join(', ') || <span style={{ color: '#9ca3af' }}>no address</span>}<br />
            {c.phone || <span style={{ color: '#9ca3af' }}>no phone</span>}{c.fax ? ` · fax ${c.fax}` : ''}
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontWeight: 800, color: keepId === id ? '#15803d' : '#374151', cursor: 'pointer' }}>
              <input type="radio" checked={keepId === id} onChange={() => setKeep(id)} /> Keep this one
            </label>
          </div>
        )}
      </div>
    );
  };
  const k = byId[keepId], f = byId[fromId];
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 5200, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '6vh 16px', overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, width: 640, maxWidth: '100%', padding: 18, boxShadow: '0 16px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
          <div style={{ fontSize: 17, fontWeight: 800, flex: 1 }}>🔗 Merge two customers</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 24, color: '#9ca3af', cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ fontSize: 12.5, color: '#6b7280', marginBottom: 12 }}>Pick the two, choose the one to keep. Everything on the other moves over and the other is removed.</div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>{side(one, setOne)}{side(two, setTwo)}</div>
        {one && two && one === two && <div style={{ color: '#b91c1c', fontSize: 13, fontWeight: 700, marginTop: 10 }}>That's the same customer twice.</div>}
        {plan && k && f && (
          <div style={{ marginTop: 12, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '10px 12px', fontSize: 13, color: '#374151', lineHeight: 1.6 }}>
            Merging <b>{f.name}</b> into <b>{k.name}</b> moves:
            <ul style={{ margin: '4px 0 0 18px', padding: 0 }}>
              <li>{plan.contacts_moved} contact{plan.contacts_moved === 1 ? '' : 's'}{plan.contacts_folded ? ` (and ${plan.contacts_folded} already on ${k.name} — kept once)` : ''}</li>
              <li>{plan.bids} bid{plan.bids === 1 ? '' : 's'} and {plan.bid_recipients} bid recipient{plan.bid_recipients === 1 ? '' : 's'}</li>
              <li>{plan.jobs} job{plan.jobs === 1 ? '' : 's'} and {plan.purchase_orders} PO{plan.purchase_orders === 1 ? '' : 's'} (renamed to {k.name})</li>
              {plan.fills.length > 0 && <li>fills {k.name}'s blank {plan.fills.map(x => ({ address1: 'address', address2: 'city/state/ZIP', phone: 'phone', fax: 'fax', notes: 'notes', contact: 'contact' }[x] || x)).join(', ')} from {f.name}</li>}
            </ul>
            <div style={{ color: '#6b7280', fontSize: 12, marginTop: 4 }}>Invoices already sent keep the name they went out with.</div>
          </div>
        )}
        {err && <div style={{ color: '#b91c1c', fontSize: 13, fontWeight: 700, marginTop: 10 }}>{err}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
          <button onClick={onClose} disabled={busy} style={{ padding: '9px 16px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
          <button onClick={merge} disabled={busy || !plan || !k || !f} style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: plan ? '#1e3a5f' : '#cbd5e1', color: '#fff', fontSize: 13, fontWeight: 800, cursor: plan ? 'pointer' : 'default' }}>
            {busy ? 'Merging…' : f && k ? `Merge into ${k.name}` : 'Merge'}
          </button>
        </div>
      </div>
    </div>
  );
}
